import React, { useState, useEffect } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';

/**
 * Componente de input con validación personalizada y mensajes consistentes
 * Suprime los tooltips del navegador y usa validación visual personalizada
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.value - Valor actual del input
 * @param {function} props.onChange - Función que se ejecuta cuando cambia el valor
 * @param {string} props.placeholder - Placeholder del input
 * @param {string} props.className - Clases CSS adicionales
 * @param {boolean} props.required - Si el campo es requerido
 * @param {string} props.name - Nombre del input
 * @param {string} props.type - Tipo del input (text, email, tel, etc.)
 * @param {boolean} props.disabled - Si el input está deshabilitado
 * @param {string} props.requiredMessage - Mensaje personalizado cuando el campo es requerido
 * @param {RegExp} props.pattern - Patrón de validación
 * @param {string} props.patternMessage - Mensaje cuando no coincide el patrón
 * @param {function} props.customValidator - Función de validación personalizada
 * @param {number} props.minLength - Longitud mínima
 * @param {number} props.maxLength - Longitud máxima
 */
const ValidatedInput = ({
  value = '',
  onChange,
  placeholder = '',
  className = '',
  required = false,
  name = '',
  type = 'text',
  disabled = false,
  requiredMessage = 'Este campo es requerido',
  pattern,
  patternMessage = 'Formato inválido',
  customValidator,
  minLength,
  maxLength,
  forceShowValidation = false, // Nueva prop para forzar mostrar validación
  ...props
}) => {
  const [hasBeenTouched, setHasBeenTouched] = useState(false);
  const [currentError, setCurrentError] = useState('');

  // Función de validación
  const validateValue = (val) => {
    // Campo requerido vacío
    if (required && (!val || val.toString().trim() === '')) {
      return requiredMessage;
    }

    // Si está vacío y no es requerido, es válido
    if (!val || val.toString().trim() === '') {
      return '';
    }

    const stringValue = val.toString();

    // Validar longitud mínima
    if (minLength && stringValue.length < minLength) {
      return `Debe tener al menos ${minLength} caracteres`;
    }

    // Validar longitud máxima
    if (maxLength && stringValue.length > maxLength) {
      return `No puede tener más de ${maxLength} caracteres`;
    }

    // Validar patrón
    if (pattern && !pattern.test(stringValue)) {
      return patternMessage;
    }

    // Validación personalizada
    if (customValidator) {
      const customResult = customValidator(val);
      if (customResult !== true) {
        return typeof customResult === 'string' ? customResult : 'Valor inválido';
      }
    }

    return '';
  };

  // Actualizar error cuando cambie el valor o forceShowValidation
  useEffect(() => {
    if (hasBeenTouched || forceShowValidation) {
      const error = validateValue(value);
      setCurrentError(error);
    }
  }, [value, hasBeenTouched, forceShowValidation]);

  // Efecto para manejar forceShowValidation
  useEffect(() => {
    if (forceShowValidation) {
      setHasBeenTouched(true);
      const error = validateValue(value);
      setCurrentError(error);
    }
  }, [forceShowValidation]);

  const showError = (hasBeenTouched || forceShowValidation) && currentError;

  // Manejar cambios en el input
  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(e);
    
    // Si ya había error y ahora es válido, limpiar error inmediatamente
    if (hasBeenTouched && currentError && !validateValue(newValue)) {
      setCurrentError('');
    }
  };

  // Manejar cuando el campo pierde el foco
  const handleBlur = () => {
    setHasBeenTouched(true);
    const error = validateValue(value);
    setCurrentError(error);
  };

  // Manejar validación inválida (prevenir tooltips del navegador)
  const handleInvalid = (e) => {
    e.preventDefault();
    setHasBeenTouched(true);
    const error = validateValue(value);
    setCurrentError(error);
  };

  // Determinar clases CSS según el estado de validación
  const getInputClasses = () => {
    let classes = `w-full p-2 rounded-lg bg-gray-700 border text-white placeholder-gray-400 focus:ring-1 transition-colors ${className}`;
    
    if (showError) {
      classes += ' input-error';
    } else {
      classes += ' border-gray-600 focus:border-blue-500 focus:ring-blue-500';
    }
    
    return classes;
  };

  return (
    <div className="relative">
      <input
        type={type}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onInvalid={handleInvalid}
        placeholder={placeholder}
        className={getInputClasses()}
        required={required}
        disabled={disabled}
        minLength={minLength}
        maxLength={maxLength}
        pattern={pattern}
        {...props}
      />
      
      {/* Mensaje de error personalizado */}
      {showError && (
        <p className="validation-message">
          <FaExclamationTriangle />
          {currentError}
        </p>
      )}
    </div>
  );
};

export default ValidatedInput;